import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { resolveUrl } from "@/lib/format";
import { LogOut, Menu as MenuIcon, X, KeyRound, UserCog } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PasswordDialog } from "@/components/PasswordDialog";
import { ProfileDialog } from "@/components/ProfileDialog";
import { NotificationBell } from "@/components/NotificationBell";

const ROLE_LABEL = { admin: "Management", staff: "Kitchen & Service", member: "Member" };

function BrandMark({ size = "lg" }) {
  const { settings } = useSettings();
  const logo = resolveUrl(settings.logo_url);
  const dim = size === "lg" ? "w-10 h-10" : "w-7 h-7";
  if (logo) return <img src={logo} alt="logo" className={`${dim} rounded-sm object-cover bg-white`} data-testid="brand-logo" />;
  return <div className={`${dim} rounded-sm bg-brand-gold flex items-center justify-center font-serif text-brand-dark text-xl font-bold`}>C</div>;
}

export function Shell({ navItems, active, onNav, children, accent = "member" }) {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const clubName = settings.club_name || "ClubTeesta";
  const [open, setOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const NavLinks = ({ onClick }) => (
    <nav className="flex flex-col gap-1" data-testid="sidebar-nav">
      {navItems.map((item) => {
        const isActive = active === item.key;
        const Icon = item.icon;
        return (
          <button
            key={item.key}
            data-testid={`nav-${item.key}`}
            onClick={() => { onNav(item.key); onClick && onClick(); }}
            className={`group flex items-center gap-3 px-4 py-3 text-sm rounded-sm transition-colors border-r-4 ${
              isActive
                ? "bg-white/10 text-white border-brand-gold"
                : "text-brand-bg/60 hover:text-white hover:bg-white/5 border-transparent"
            }`}
          >
            <Icon className={`w-[18px] h-[18px] ${isActive ? "text-brand-gold" : ""}`} />
            <span className="font-medium">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen flex bg-brand-bg">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex flex-col w-64 shrink-0 bg-brand-primary sticky top-0 h-screen">
        <div className="p-6 flex items-center gap-3 border-b border-white/10">
          <BrandMark size="lg" />
          <div className="flex-1 min-w-0">
            <p className="text-brand-bg font-serif text-lg leading-none tracking-tight truncate">{clubName}</p>
            <p className="text-brand-gold text-[10px] uppercase tracking-[0.2em] mt-1">{ROLE_LABEL[accent]}</p>
          </div>
          <NotificationBell variant="dark" />
        </div>
        <div className="flex-1 py-5 px-3 overflow-y-auto"><NavLinks /></div>
        <div className="p-4 border-t border-white/10">
          <div className="mb-3 px-2">
            <p className="text-white text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</p>
            <p className="text-brand-bg/50 text-xs truncate font-mono">{user?.username}</p>
          </div>
          <Button onClick={() => setProfileOpen(true)} data-testid="my-profile-button" variant="ghost"
            className="w-full justify-start gap-2 text-brand-bg/70 hover:text-white hover:bg-white/10 rounded-sm">
            <UserCog className="w-4 h-4" /> My profile
          </Button>
          <Button onClick={() => setPwOpen(true)} data-testid="change-password-button" variant="ghost"
            className="w-full justify-start gap-2 text-brand-bg/70 hover:text-white hover:bg-white/10 rounded-sm">
            <KeyRound className="w-4 h-4" /> Change password
          </Button>
          <Button onClick={logout} data-testid="logout-button" variant="ghost"
            className="w-full justify-start gap-2 text-brand-bg/70 hover:text-white hover:bg-white/10 rounded-sm">
            <LogOut className="w-4 h-4" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 bg-brand-primary flex items-center justify-between px-4 h-14">
        <div className="flex items-center gap-2">
          <BrandMark size="sm" />
          <span className="text-brand-bg font-serif text-lg">{clubName}</span>
        </div>
        <div className="flex items-center gap-1">
          <NotificationBell variant="dark" />
          <button onClick={() => setOpen(true)} data-testid="mobile-menu-button" className="text-brand-bg p-2"><MenuIcon className="w-6 h-6" /></button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="relative w-72 max-w-[80%] bg-brand-primary h-[100dvh] max-h-[100dvh] flex flex-col animate-fade-up">
            <div className="shrink-0 p-5 flex items-center justify-between border-b border-white/10">
              <span className="text-brand-bg font-serif text-xl">{clubName}</span>
              <button onClick={() => setOpen(false)} className="text-brand-bg"><X className="w-6 h-6" /></button>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain" data-testid="mobile-drawer-scroll"
              style={{ paddingBottom: "max(3rem, env(safe-area-inset-bottom))" }}>
              <div className="py-4 px-3"><NavLinks onClick={() => setOpen(false)} /></div>
              <div className="p-4 border-t border-white/10">
                <p className="text-white text-sm font-semibold px-2">{user?.name}</p>
                <p className="text-brand-bg/50 text-xs px-2 font-mono truncate">{user?.username}</p>
                <Button onClick={() => { setOpen(false); setProfileOpen(true); }} data-testid="my-profile-button-mobile" variant="ghost" className="w-full justify-start gap-2 mt-2 text-brand-bg/70 hover:text-white hover:bg-white/10">
                  <UserCog className="w-4 h-4" /> My profile
                </Button>
                <Button onClick={() => { setOpen(false); setPwOpen(true); }} data-testid="change-password-button-mobile" variant="ghost" className="w-full justify-start gap-2 mt-2 text-brand-bg/70 hover:text-white hover:bg-white/10">
                  <KeyRound className="w-4 h-4" /> Change password
                </Button>
                <Button onClick={logout} data-testid="logout-button-mobile" variant="ghost" className="w-full justify-start gap-2 mt-2 text-brand-bg/70 hover:text-white hover:bg-white/10">
                  <LogOut className="w-4 h-4" /> Sign out
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 md:pt-0">{children}</main>
      <PasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
      <ProfileDialog open={profileOpen} onClose={() => setProfileOpen(false)} />
    </div>
  );
}
